/**
 * Fonte exportável: Fluxos — Sessões
 * -----------------------------------------------------------------------------
 * Exporta as sessões de um fluxo (aba Resultado e modal "Sessões no node" do
 * Analytics) respeitando o controle padrão de exportação.
 *
 * NÃO existe RPC dedicada: a leitura reaproveita `get_flow_results_paginated`
 * com `p_export_mode = true`, que valida permissão (assert_export_allowed
 * 'flow_sessions'), limite de uso e libera blocos de até 500 linhas.
 *
 * As colunas dos nodes são dinâmicas (uma por node interativo do fluxo) e
 * chegam nos parâmetros da tela (`nodeColumns`).
 *
 * @version 1.0.0
 * RELEASE: 2026-10-02 v1.0.0 - Criação inicial.
 */

import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';
import { EXPORT_ROW_LIMIT, type ExportColumn, type ExportParamSummary, type ExportSource } from '../types';

const logger = createLogger('ExportFlowSessions');

/** Tamanho do bloco de leitura (teto do modo exportação na RPC) */
const LOTE = 500;

/** Prefixo das colunas dinâmicas de resposta por node */
export const FLOW_NODE_COLUMN_PREFIX = 'node__';

/** Parâmetros da tela enviados ao modal/servidor */
export interface FlowSessionsExportParams {
  flowId: string;
  flowName?: string | null;
  nodeId?: string | null;
  nodeLabel?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  inboxId?: string | null;
  inboxName?: string | null;
  triggerType?: string | null;
  status?: string | null;
  source?: string | null;
  search?: string | null;
  /** Nodes interativos do fluxo (colunas dinâmicas) */
  nodeColumns?: Array<{ nodeId: string; label: string }>;
}

/** Rótulos legíveis (espelham a tabela da tela) */
const GATILHOS: Record<string, string> = {
  link: 'Link', keyword: 'Keyword', first_message: '1ª Mensagem', scheduled: 'Agendado',
  inactivity: 'Inatividade', event: 'Evento', webhook: 'Webhook', phase: 'Fase',
  contact_tag: 'Tag Contato', department: 'Departamento', active_conversation: 'Conversa Ativa',
  any_message: 'Qualquer Msg', crm_filter: 'Filtro CRM', welcome_flow: 'Boas-vindas',
  contract_event: 'Contrato',
};
const SITUACOES: Record<string, string> = {
  completed: 'Concluído', running: 'Em andamento', failed: 'Falhou', paused: 'Pausado',
  cancelled: 'Cancelado', waiting_input: 'Aguardando', waiting_delay: 'Aguardando',
  in_progress: 'Em andamento',
};
const ORIGENS: Record<string, string> = { whatsapp: 'Inbox', link: 'Link' };

/** Colunas fixas */
const COLUNAS_BASE: ExportColumn[] = [
  { key: 'origem', label: 'Origem', group: 'Sessão', defaultSelected: true },
  { key: 'tipo', label: 'Tipo', group: 'Sessão', defaultSelected: true },
  { key: 'inbox', label: 'Inbox', group: 'Sessão', defaultSelected: true },
  { key: 'gatilho', label: 'Gatilho', group: 'Sessão', defaultSelected: true },
  { key: 'contato', label: 'Contato / Visitante', group: 'Contato', defaultSelected: true },
  { key: 'telefone', label: 'Telefone', group: 'Contato', defaultSelected: true },
  { key: 'status', label: 'Status', group: 'Sessão', defaultSelected: true },
  { key: 'inicio', label: 'Início', group: 'Datas', kind: 'datetime', defaultSelected: true },
  { key: 'fim', label: 'Fim', group: 'Datas', kind: 'datetime', defaultSelected: true },
  { key: 'duracao', label: 'Duração', group: 'Datas', defaultSelected: true },
];

/** Duração legível entre início e fim */
function duracao(inicio: string | null, fim: string | null): string | null {
  if (!inicio || !fim) return null;
  const s = Math.floor((new Date(fim).getTime() - new Date(inicio).getTime()) / 1000);
  if (s < 0) return null;
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

interface LinhaSessao {
  source: string | null;
  trigger_type: string | null;
  trigger_detail: string | null;
  contact_name: string | null;
  phone: string | null;
  status: string | null;
  started_at: string | null;
  completed_at: string | null;
  inbox_name: string | null;
  step_responses: Record<string, string> | null;
  total_count: number | null;
}

/** Converte a linha da RPC na linha "plana" do arquivo */
function achatar(l: LinhaSessao): Record<string, unknown> {
  const plana: Record<string, unknown> = {
    origem: ORIGENS[l.source || ''] || l.source,
    tipo: GATILHOS[l.trigger_type || ''] || l.trigger_type,
    inbox: l.inbox_name,
    gatilho: l.trigger_detail,
    contato: l.contact_name,
    telefone: l.phone,
    status: SITUACOES[l.status || ''] || l.status,
    inicio: l.started_at,
    fim: l.completed_at,
    duracao: duracao(l.started_at, l.completed_at),
  };
  for (const [nodeId, valor] of Object.entries(l.step_responses || {})) {
    plana[`${FLOW_NODE_COLUMN_PREFIX}${nodeId}`] = valor;
  }
  return plana;
}

/** Chama a RPC existente em modo exportação */
async function buscarBloco(tenantId: string, p: FlowSessionsExportParams, pagina: number, tamanho: number) {
  const { data, error } = await (supabase.rpc as any)('get_flow_results_paginated', {
    p_flow_id: p.flowId,
    p_page: pagina,
    p_page_size: tamanho,
    p_sort_field: 'started_at',
    p_sort_direction: 'desc',
    p_start_date: p.startDate || null,
    p_end_date: p.endDate || null,
    p_inbox_id: p.inboxId || null,
    p_trigger_type: p.triggerType || null,
    p_status: p.status || null,
    p_search: p.search || null,
    p_source: p.source && p.source !== 'all' ? p.source : null,
    p_node_id: p.nodeId || null,
    p_export_mode: true,
    p_tenant_hint: tenantId,
  });
  if (error) throw error;
  return (data || []) as LinhaSessao[];
}

export const flowSessionsExportSource: ExportSource<FlowSessionsExportParams> = {
  id: 'flow_sessions',
  label: 'Fluxos — Sessões',
  title: 'Fluxos — Sessões',
  moduleKey: 'conversas',
  fileBaseName: 'sessoes-fluxo',
  columns: COLUNAS_BASE,

  resolveColumns: async (_tenantId, p) => [
    ...COLUNAS_BASE,
    ...(p.nodeColumns || []).map((n) => ({
      key: `${FLOW_NODE_COLUMN_PREFIX}${n.nodeId}`,
      label: n.label,
      group: 'Respostas dos nodes',
      defaultSelected: true,
    })),
  ],

  fetchRows: async (tenantId, p) => {
    const linhas: Record<string, unknown>[] = [];
    for (let pagina = 1; linhas.length < EXPORT_ROW_LIMIT; pagina++) {
      const bloco = await buscarBloco(tenantId, p, pagina, LOTE);
      linhas.push(...bloco.map(achatar));
      if (bloco.length < LOTE) break;
    }
    logger.debug(`Exportação de sessões: ${linhas.length} registro(s)`);
    return linhas.slice(0, EXPORT_ROW_LIMIT);
  },

  countRows: async (tenantId, p) => {
    const bloco = await buscarBloco(tenantId, p, 1, 1);
    return Number(bloco[0]?.total_count ?? 0);
  },

  describeParams: async (_tenantId, p) => {
    const r: ExportParamSummary[] = [];
    if (p.flowName) r.push({ group: 'filtro', label: 'Fluxo', value: p.flowName });
    if (p.nodeLabel) r.push({ group: 'filtro', label: 'Node', value: p.nodeLabel });
    if (p.source && p.source !== 'all') r.push({ group: 'filtro', label: 'Origem', value: ORIGENS[p.source] || p.source });
    if (p.inboxId) r.push({ group: 'filtro', label: 'Inbox', value: p.inboxName || 'Selecionada' });
    if (p.triggerType) r.push({ group: 'filtro', label: 'Tipo', value: GATILHOS[p.triggerType] || p.triggerType });
    if (p.status) r.push({ group: 'filtro', label: 'Status', value: SITUACOES[p.status] || p.status });
    if (p.search) r.push({ group: 'filtro', label: 'Busca', value: p.search });
    if (p.startDate || p.endDate) {
      const f = (d?: string | null) => (d ? new Date(d).toLocaleDateString('pt-BR') : '…');
      r.push({ group: 'filtro', label: 'Período', value: `${f(p.startDate)} a ${f(p.endDate)}` });
    }
    r.push({ group: 'ordenacao', label: 'Ordenado por', value: 'Início — mais recentes primeiro' });
    return r;
  },
};
